import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { postsService } from "../../../services/api/postsService";
import CustomTable from "../components/CustomTable";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import TableSkeleton from "../components/TableSkeleton";
import { StatusBadge } from "../../../constants/statusConstants";
import { getCleanFileName } from "../../../utils/fileUtils";
import MobilePatternHeader from "../components/MobilePatternHeader";
import { getPostFileUrl, getPostsListPayload } from "./utils/postResponseUtils";
import EmptyState from "../components/EmptyState";

const PostsManagement = ({ isMobile }) => {
  const { token_client } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page")) || 1;
  const [searchInput, setSearchInput] = useState(search);

  const fetchData = useCallback(
    async (searchValue = "", currentPage = 1) => {
      try {
        setLoading(true);
        const response = await postsService.get(
          searchValue,
          token_client,
          currentPage,
        );
        const { posts: postsData, pagination } = getPostsListPayload(response);

        setPosts(postsData);
        setTotalPages(pagination.totalPages || 1);
      } catch (error) {
        toast.dismiss();
        toast.error(
          error.response?.data?.message || "Không thể kết nối tới server",
        );
      } finally {
        setLoading(false);
      }
    },
    [token_client],
  );

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    if (searchInput.trim()) {
      params.set("search", searchInput.trim());
    } else {
      params.delete("search");
    }
    params.set("page", "1");
    setSearchParams(params);
  }, [searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchInput !== (searchParams.get("search") || "")) {
        handleSearch();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, searchParams, handleSearch]);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (!token_client) return;
    fetchData(search, page);
  }, [fetchData, search, page, token_client]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "stt",
        title: "STT",
        minWidth: "60px",
        align: "center",
        render: (_, __, index) => (page - 1) * 10 + index + 1,
      },
      {
        key: "name",
        title: "Tên tài liệu",
        minWidth: "250px",
        render: (value) => <p className="truncate font-medium">{value}</p>,
      },
      {
        key: "txtUrl",
        title: "Nội dung bài viết",
        minWidth: "150px",
        render: (value) => {
          if (!value || value.trim() === "" || value === "<p><br></p>") {
            return <span className="text-gray-400">Không có bài viết</span>;
          }
          return (
            <span className="inline-block text-blue-600 hover:text-blue-700 font-medium hover:underline cursor-pointer transition-colors">
              Xem nội dung
            </span>
          );
        },
      },
      {
        key: "fileUrl",
        title: "File đính kèm",
        minWidth: "150px",
        render: (value, item) => {
          const fileUrl = value || getPostFileUrl(item);

          if (!fileUrl) {
            return <span className="text-gray-400">--</span>;
          }
          return (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-blue-600 hover:text-blue-700 font-medium hover:underline cursor-pointer transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {getCleanFileName(fileUrl)}
            </a>
          );
        },
      },
      {
        key: "status",
        title: "Trạng thái",
        minWidth: "120px",
        render: (value) => <StatusBadge status={value || "Hoạt động"} />,
      },
    ],
    [page],
  );

  if (isMobile) {
    return (
      <div className="h-full w-full">
        <MobilePatternHeader
          title="Quản lý bài viết"
          onBack={() => navigate(-1)}
        />

        <div className="h-[calc(100%-64px)] bg-[#F2F2F2]">
          <div className="relative flex w-full items-center p-3">
            <input
              type="text"
              placeholder="Tìm kiếm bài viết"
              className="w-full rounded-3xl border border-gray-200 px-4 py-2 focus:outline-blue-500"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button
              className="absolute right-6 p-1"
              onClick={handleSearch}
              disabled={loading}
            >
              <img src="/icons/search-black.svg" alt="" />
            </button>
          </div>

          <div className="h-[calc(100%-60px)] overflow-auto px-2">
            {loading ? (
              <TableSkeleton rows={3} />
            ) : posts.length > 0 ? (
              <CustomTable
                data={posts}
                columns={columns.filter((col) => col.key !== "stt")}
                currentPage={page}
                setCurrentPage={(newPage) => {
                  const params = new URLSearchParams(searchParams);
                  params.set("page", newPage);
                  setSearchParams(params);
                }}
                totalPages={totalPages}
                setView
                onRowClick={(item) =>
                  navigate(`/tai-lieu/bai-viet/${item._id}`)
                }
                rowIdKey="_id"
              />
            ) : (
              <EmptyState description="Không tìm thấy bài viết phù hợp" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="h-full min-h-0 flex flex-col"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
    >
      <div className="shrink-0">
        <div className="flex items-center justify-between">
          <div className="relative w-1/3">
            <input
              type="text"
              placeholder="Tìm kiếm tài liệu"
              className="w-full rounded-lg border border-gray-200 px-4 py-2 focus:outline-blue-500"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <span
              className="absolute right-3 top-2.5 cursor-pointer"
              onClick={handleSearch}
            >
              <img src="/icons/search-black.svg" alt="Search icon" />
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto mt-4">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="w-full px-2">
              <TableSkeleton rows={3} />
            </div>
          </div>
        ) : posts.length > 0 ? (
          <motion.div
            className="h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            <CustomTable
              data={posts}
              columns={columns}
              currentPage={page}
              setCurrentPage={(newPage) => {
                const params = new URLSearchParams(searchParams);
                params.set("page", newPage);
                setSearchParams(params);
              }}
              totalPages={totalPages}
              setView
              onRowClick={(item) => navigate(`/tai-lieu/bai-viet/${item._id}`)}
              rowIdKey="_id"
              rowClassName="transition-colors hover:bg-gray-50"
            />
          </motion.div>
        ) : (
          <EmptyState description="Không tìm thấy bài viết phù hợp" />
        )}
      </div>
    </motion.div>
  );
};

export default PostsManagement;
